import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/landing/Logo";
import {
  LogOut, Users, Inbox, Settings as SettingsIcon, Kanban, IdCard, ListChecks, BarChart3,
} from "lucide-react";

export function AdminHeader() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  // Single-line tabs — whitespace-nowrap prevents "My Tasks" from wrapping to
  // two lines on narrow desktop widths and keeps the header row height uniform.
  const tabBase =
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.16em] font-semibold border transition-colors whitespace-nowrap";

  const tabCls = ({ isActive }) =>
    `${tabBase} ${isActive ? "bg-brand-orange text-white border-brand-orange" : "border-white/30 text-white/80 hover:bg-white/10"}`;

  const tabs = [
    { to: "/admin", end: true, testid: "admin-tab-leads", Icon: Inbox, label: "Leads" },
    { to: "/admin/pipeline", testid: "admin-tab-pipeline", Icon: Kanban, label: "Pipeline" },
    { to: "/admin/tasks", testid: "admin-tab-tasks", Icon: ListChecks, label: "My Tasks" },
    { to: "/admin/contacts", testid: "admin-tab-contacts", Icon: IdCard, label: "Contacts" },
    { to: "/admin/reports", testid: "admin-tab-reports", Icon: BarChart3, label: "Reports", adminOnly: true },
    { to: "/admin/team", testid: "admin-tab-team", Icon: Users, label: "Team", adminOnly: true },
    { to: "/admin/settings", testid: "admin-tab-settings", Icon: SettingsIcon, label: "Settings", adminOnly: true },
  ];

  const visibleTabs = tabs.filter((t) => !t.adminOnly || admin?.role === "admin");

  return (
    <header className="bg-brand-navy text-white border-b border-white/10" data-testid="admin-header">
      <div className="max-w-[1600px] mx-auto px-6 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="admin-home-link" className="shrink-0">
          <Logo tone="light" size="sm" />
        </Link>

        <nav className="hidden lg:flex items-center gap-2 flex-nowrap min-w-0">
          {visibleTabs.map(({ to, end, testid, Icon, label }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid} className={tabCls}>
              <Icon size={13} /> {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <span className="hidden sm:flex flex-col items-end leading-tight whitespace-nowrap">
            <span className="text-xs text-white/85">{admin?.name || admin?.email}</span>
            <span className="text-[9px] uppercase tracking-[0.2em] text-brand-orange">{admin?.role}</span>
          </span>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 text-xs text-white/85 hover:text-brand-orange whitespace-nowrap"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {/* Mobile / tablet tabs — horizontal scroll below the top bar */}
      <div className="lg:hidden border-t border-white/10 px-6 py-2 flex gap-2 overflow-x-auto">
        {visibleTabs.map(({ to, end, testid, Icon, label }) => (
          <NavLink key={to} to={to} end={end} data-testid={`${testid}-mobile`} className={tabCls}>
            <Icon size={13} /> {label}
          </NavLink>
        ))}
      </div>
    </header>
  );
}
